"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import { useConvexAuth } from "convex/react";
import { FileText, Timer, CheckCircle2, Lightbulb, Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import LaTeXRenderer from "@/components/LaTeXRenderer";
import { useBoard } from "@/components/BoardProvider";
import { useAuthModal } from "@/auth/auth-modal-provider";
import { captureClientEvent } from "@/lib/analytics/client";
import { DEMO_QUESTIONS } from "./demoQuestions";

/**
 * Try-a-real-question demo: a 1:1 emulation of the exam session client
 * (same header chips, progress bar, split panes, option rows and toolbar as
 * ExamSessionClient) running on hardcoded real past-paper questions, with a
 * check-answer step, worked solution and signup CTA. No auth required.
 */

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function TryQuestionSection() {
  const board = useBoard();
  const { openAuthModal } = useAuthModal();
  const { isAuthenticated } = useConvexAuth();

  const questions = DEMO_QUESTIONS[board.id] ?? DEMO_QUESTIONS.TMUA;

  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState<Record<number, string>>({});
  const [checked, setChecked] = useState<Record<number, boolean>>({});
  const [flagged, setFlagged] = useState<number[]>([]);
  const [hintNudge, setHintNudge] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [solutionOpen, setSolutionOpen] = useState(false);

  const q = questions[index];
  const answeredCount = Object.keys(checked).length;
  const done = answeredCount >= questions.length;
  const isChecked = Boolean(checked[index]);
  const choice = selected[index];

  useEffect(() => {
    if (done) return;
    const t = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [done]);

  const handleCheck = () => {
    if (!choice || isChecked) return;
    setChecked((c) => ({ ...c, [index]: true }));
    setSolutionOpen(true);
    captureClientEvent("landing_demo_question_answered", {
      board: board.id,
      questionNumber: q.questionNumber,
      correct: choice === q.answer,
      secondsElapsed: seconds,
    });
  };

  const handleCta = () => {
    captureClientEvent("landing_demo_cta_clicked", { board: board.id });
    openAuthModal("signup");
  };

  const optionState = (key: string): "chosen" | "correct" | "wrong" | "idle" => {
    if (!isChecked) return choice === key ? "chosen" : "idle";
    if (key === q.answer) return "correct";
    if (key === choice) return "wrong";
    return "idle";
  };

  return (
    <section className="px-4 pt-20 md:pt-24 pb-8 md:pb-12">
      <h2 className="text-center text-3xl md:text-5xl font-script text-foreground px-6">
        Try a real question
      </h2>
      <p className="mt-4 mb-10 text-center text-muted-foreground max-w-xl mx-auto font-polysans text-base md:text-lg">
        Sort the whole bank by topic and drill your weakest areas, in the real exam interface.
      </p>

      {/* The emulated exam client */}
      <div className="relative mx-auto max-w-6xl overflow-hidden rounded-2xl border border-border/60 bg-background shadow-sm p-3 sm:p-4 lg:p-5">
        {/* Session header — mirrors ExamSessionClient */}
        <div className="mb-4">
          <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2 bg-primary/10 rounded-lg">
                <FileText className="w-5 h-5 text-primary" />
              </div>
              <div className="flex items-center gap-3 flex-wrap min-w-0">
                <h3 className="font-bold text-lg sm:text-xl md:text-2xl text-foreground">
                  {q.subject}
                </h3>
                <span className="text-sm text-muted-foreground font-medium">{q.paperMeta}</span>
                <span className="text-sm text-muted-foreground">{q.year}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="flex items-center gap-2 bg-card border rounded-lg px-3 py-2">
                <Timer className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium tabular-nums">{formatTime(seconds)}</span>
              </div>
              <div className="flex items-center gap-2 bg-card border rounded-lg px-3 py-2">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <span className="text-sm font-medium">
                  {answeredCount}/{questions.length} answered
                </span>
              </div>
            </div>
          </div>
          <div className="mt-3">
            <Progress value={(answeredCount / questions.length) * 100} className="h-3 rounded-full" />
            {/* The chips above already show the count; on mobile this label row
                is a third copy of the same fact, so it only shows from sm up. */}
            <div className="hidden sm:flex justify-between mt-2">
              <span className="text-xs text-muted-foreground">Progress</span>
              <span className="text-xs text-muted-foreground">
                {Math.round((answeredCount / questions.length) * 100)}% complete
              </span>
            </div>
          </div>
        </div>

        {/* Question + options — the exam client's split panes */}
        <div className="grid items-start gap-4 lg:grid-cols-[1.1fr_12px_1fr] lg:gap-0">
          <div className="relative rounded-xl border border-border/60 bg-card p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-base font-semibold">Question {q.questionNumber}</div>
                <div className="text-sm text-muted-foreground">1 mark</div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setHintNudge((h) => !h)}
                  className="w-9 h-9 rounded-xl"
                >
                  <Lightbulb className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() =>
                    setFlagged((f) => (f.includes(index) ? f.filter((i) => i !== index) : [...f, index]))
                  }
                  className={`w-9 h-9 rounded-xl transition-all duration-200 ${flagged.includes(index) ? "text-red-500 border-red-500 bg-red-50 hover:bg-red-100" : ""}`}
                >
                  <Flag className="w-4 h-4" />
                </Button>
              </div>
            </div>
            {hintNudge && (
              <p className="mb-2 text-sm italic text-muted-foreground">
                Hints come with a free account. Sign up and you get them on every question in the bank.
              </p>
            )}
            <div className="text-lg leading-relaxed">
              <LaTeXRenderer content={q.text} />
            </div>
          </div>

          {/* Gutter (decorative here; draggable in the real client) */}
          <div className="hidden lg:flex items-stretch justify-center">
            <div className="w-3 h-full rounded-full bg-border" />
          </div>

          <div className="space-y-2">
            {Object.entries(q.options).map(([key, value]) => {
              const state = optionState(key);
              const rowStyle =
                state === "correct"
                  ? { borderColor: "var(--success)", background: "color-mix(in oklab, var(--success) 7%, transparent)" }
                  : state === "wrong"
                    ? { borderColor: "var(--destructive)", background: "color-mix(in oklab, var(--destructive) 7%, transparent)" }
                    : undefined;
              return (
                <div
                  key={key}
                  onClick={() => !isChecked && setSelected((s) => ({ ...s, [index]: key }))}
                  style={{ minHeight: "3.5rem", ...rowStyle }}
                  className={`group p-3 rounded-xl transition-all duration-200 border-2 ${isChecked ? "cursor-default" : "cursor-pointer"} ${
                    state === "chosen"
                      ? "border-primary bg-primary/5 shadow-lg"
                      : state === "idle"
                        ? `border-border ${isChecked ? "" : "hover:border-primary/60 hover:shadow-md hover:bg-accent/50"}`
                        : ""
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-8 h-8 rounded-full border-2 flex items-center justify-center font-bold text-base transition-all duration-200 flex-shrink-0 ${
                        state === "chosen"
                          ? "border-primary bg-primary text-primary-foreground shadow-sm"
                          : state === "idle"
                            ? `border-muted-foreground bg-background ${isChecked ? "" : "group-hover:border-primary"}`
                            : "text-white shadow-sm"
                      }`}
                      style={
                        state === "correct"
                          ? { borderColor: "var(--success)", background: "var(--success)" }
                          : state === "wrong"
                            ? { borderColor: "var(--destructive)", background: "var(--destructive)" }
                            : undefined
                      }
                    >
                      <span className="text-sm font-medium">{key}</span>
                    </div>
                    <div className="flex-1">
                      <LaTeXRenderer content={value} className="text-base" />
                    </div>
                  </div>
                </div>
              );
            })}

            {!isChecked ? (
              <Button
                onClick={handleCheck}
                disabled={!choice}
                className="mt-1 h-14 w-full rounded-xl text-base font-semibold sm:h-14"
              >
                Check answer
              </Button>
            ) : (
              <div className="flex items-center justify-between gap-3 pt-1">
                <ResultChip correct={choice === q.answer} answer={q.answer} />
                <Button
                  variant="outline"
                  className="h-9 rounded-xl"
                  onClick={() => setSolutionOpen(true)}
                >
                  View solution
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Worked solution — a real dialog over the client, not a see-through
            overlay */}
        <Dialog open={isChecked && solutionOpen} onOpenChange={setSolutionOpen}>
          <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base font-medium">
                <Lightbulb className="size-4" style={{ color: "var(--warning)" }} />
                Worked solution
              </DialogTitle>
            </DialogHeader>

            <div>
              <ResultChip correct={choice === q.answer} answer={q.answer} />
            </div>

            {/* KaTeX gives every display block its own 1em margin on top of the
                renderer's my-6 wrapper, which reads as a gap on a phone. */}
            <div className="rounded-xl border border-border/60 bg-muted/30 p-4 sm:p-5 text-sm md:text-base leading-relaxed text-foreground [&_.katex-display]:my-0">
              <LaTeXRenderer content={q.workedSolution} />
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              {index < questions.length - 1 ? (
                <Button
                  variant="outline"
                  className="h-11 rounded-xl sm:flex-1"
                  onClick={() => {
                    setHintNudge(false);
                    setSolutionOpen(false);
                    setIndex((i) => i + 1);
                  }}
                >
                  Next question
                </Button>
              ) : null}
              {isAuthenticated ? (
                <Link
                  href="/dashboard/questions"
                  className="inline-flex h-11 items-center justify-center rounded-xl bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 sm:flex-1"
                >
                  Keep practising
                </Link>
              ) : (
                <Button onClick={handleCta} className="h-11 rounded-xl sm:flex-1">
                  Practise the full bank free
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
}

function ResultChip({ correct, answer }: { correct: boolean; answer: string }) {
  return (
    <div
      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium"
      style={{
        background: `color-mix(in oklab, var(--${correct ? "success" : "destructive"}) 12%, transparent)`,
        color: `var(--${correct ? "success" : "destructive"})`,
      }}
    >
      <CheckCircle2 className="size-4" />
      {correct ? "Correct. Nicely done." : `Not quite. The answer is ${answer}.`}
    </div>
  );
}
